export type Size = 'large' | 'small';
export type Length = 'short' | 'medium' | 'long';
export type Variant =
  | 'page'
  | 'previous'
  | 'next'
  | 'before-ellipsis'
  | 'after-ellipsis';

const MAX_SLOTS_MAP: Record<Length, Record<Size, number>> = {
  short: { large: 6, small: 7 },
  medium: { large: 9, small: 11 },
  long: { large: 12, small: 15 },
};

// https://dev.to/namirsab/comment/2050
/**
 * Returns an array with elements from `start` to `end`, inclusive
 */
const range = (start: number, end: number) => {
  const length = end - start + 1;

  return Array.from({ length }, (_, i) => start + i);
};

interface GetItemsOptions {
  length: Length;
  currentPage: number;
  itemSize: Size;
  total: number;
}
type Item =
  | { variant: 'page'; page: number; isSelected: boolean }
  | {
      variant: 'previous' | 'next';
      disabled: boolean;
      /**
       * the resultant page after clicking the button.
       * `undefined` if already at the edge
       */
      page?: number;
    }
  | {
      variant: 'before-ellipsis' | 'after-ellipsis';
      /**
       * the first page the ellipsis covers
       */
      start: number;
      /**
       * the last page the ellipsis covers
       */
      end: number;
    };

const getAllItems = (length: Length, currentPage: number, total: number) => {
  const items: Item[] = [];
  let hasPrevious = false;
  let hasNext = false;

  // short length hides previous button when on the first page
  // its disabled on longer lengths
  if (length !== 'short' || currentPage > 1) {
    const disabled = currentPage === 1;

    items.push({
      variant: 'previous',
      disabled,
      page: disabled ? undefined : currentPage - 1,
    });
    hasPrevious = true;
  }

  items.push(
    ...range(1, total).map(
      (page): Item => ({
        variant: 'page',
        page,
        isSelected: page === currentPage,
      }),
    ),
  );

  // short length hides next button when on the last page
  // its disabled on longer lengths
  if (length !== 'short' || currentPage < total) {
    const disabled = currentPage === total;

    items.push({
      variant: 'next',
      disabled,
      page: disabled ? undefined : currentPage + 1,
    });
    hasNext = true;
  }

  return { items, hasPrevious, hasNext };
};

/**
 * Gets the pagination items to display based on the specified total
 * and length, plus the currently selected page
 */
export const getItems = ({
  length,
  currentPage: page,
  itemSize,
  total,
}: GetItemsOptions): Item[] => {
  // slot priority:
  // page (current)
  // previous
  // next
  // page (first)
  // page (last)
  // page (next)
  // page (previous)
  // pages (before)
  // pages (after)
  if (total <= 1) {
    return [];
  }

  // ensure page is within range
  const currentPage = Math.max(Math.min(page, total), 1);

  const maxSlots = MAX_SLOTS_MAP[length][itemSize];
  const { items, hasPrevious, hasNext } = getAllItems(
    length,
    currentPage,
    total,
  );
  const numSlotsOver = items.length - maxSlots;

  if (!maxSlots || numSlotsOver <= 0) {
    return items;
  }

  const currentPageIndex = items.findIndex(
    item => item.variant === 'page' && item.page === currentPage,
  );
  // if there is a previous button, first page is at index 1 (2nd item)
  // otherwise it's at index 0 (1st item)
  const firstPageIndex = hasPrevious ? 1 : 0;
  // last page is either the second to last item (when there's a next)
  // or the last item (when next is excluded)
  const lastPageIndex = items.length - 1 - (hasNext ? 1 : 0);
  // the page before the current page is -1, but ensure that
  // it's not less than the first page
  const previousPageIndex = Math.max(currentPageIndex - 1, firstPageIndex);
  // the page after the current page is +1, but ensure that
  // it's not greater than the last page
  const nextPageIndex = Math.min(currentPageIndex + 1, lastPageIndex);

  // initialize the initial ranges for the after & before
  // ellipses. we'll adjust them to make sure we make the
  // ellipses small enough to show as many pages as possible,
  // but big enough that we don't go over the max number
  // of slots
  let beforeEllipsisStartIndex = firstPageIndex + 1;
  let beforeEllipsisEndIndex = previousPageIndex - 1;
  let afterEllipsisStartIndex = nextPageIndex + 1;
  let afterEllipsisEndIndex = lastPageIndex - 1;

  const getBeforeEllipsisSize = () =>
    Math.max(beforeEllipsisEndIndex - beforeEllipsisStartIndex + 1, 0);
  const getAfterEllipsisSize = () =>
    Math.max(afterEllipsisEndIndex - afterEllipsisStartIndex + 1, 0);
  const getEllipsesDifference = () => {
    // return the number of extra (or missing) ellipsis pages.
    // a positive number means we have too many pages collapsed in ellipses.
    // a negative number means we don't have enough pages in ellipses.
    // we also need to account for the fact that the ellipses themselves
    // will take up a slot (hence the - 1).
    const beforeSize = Math.max(getBeforeEllipsisSize() - 1, 0);
    const afterSize = Math.max(getAfterEllipsisSize() - 1, 0);

    return beforeSize + afterSize - numSlotsOver;
  };

  const removePagesFromEllipses = () => {
    // if there's room in the before ellipsis, remove the
    // last page captured in the ellipsis. this biases
    // towards displaying pages close to the selected page
    if (beforeEllipsisEndIndex > beforeEllipsisStartIndex) {
      beforeEllipsisEndIndex -= 1;

      return;
    }

    // if there's room in the after ellipsis, remove the
    // first page captured in the ellipsis. this biases
    // towards displaying pages close to the selected page
    if (afterEllipsisStartIndex < afterEllipsisEndIndex) {
      afterEllipsisStartIndex += 1;
    }
  };
  const addPagesToEllipses = () => {
    // we try to keep the previous, next, last & first pages
    // but if we still have too many slots to display we have
    // to remove them in reverse priority order.
    // the order of these if statements determines their priority.
    // first if has lowest priority.

    // first try to add the previous page to the before ellipsis
    if (beforeEllipsisEndIndex < previousPageIndex) {
      beforeEllipsisEndIndex = previousPageIndex;

      return;
    }

    // next try to add the next page to the after ellipsis
    if (afterEllipsisStartIndex > nextPageIndex) {
      afterEllipsisStartIndex = nextPageIndex;

      return;
    }

    // third, try to add the last page to the after ellipsis
    if (afterEllipsisEndIndex < lastPageIndex) {
      afterEllipsisEndIndex = lastPageIndex;

      return;
    }

    // lastly, try to add the first page to the before ellipsis
    if (beforeEllipsisStartIndex > firstPageIndex) {
      beforeEllipsisStartIndex = firstPageIndex;
    }
  };

  let ellipsesDifference = getEllipsesDifference();

  while (ellipsesDifference !== 0) {
    if (ellipsesDifference > 0) {
      // ellipses are too big such that there are empty slots
      removePagesFromEllipses();
    } else if (ellipsesDifference < 0) {
      // ellipses are too small such that there are more slots
      // than allowed
      addPagesToEllipses();
    }

    ellipsesDifference = getEllipsesDifference();
  }

  // ellipses sizes have to be greater than 1 to be worth
  // adding. otherwise we would just display the page.
  // also return which pages are covered by the ellipses.
  // when there is a previous button the indices match the
  // page numbers, but when it's hidden on small length we
  // have to adjust
  if (getAfterEllipsisSize() > 1) {
    items.splice(afterEllipsisStartIndex, getAfterEllipsisSize(), {
      variant: 'after-ellipsis',
      start: afterEllipsisStartIndex + (!hasPrevious ? 1 : 0),
      end: afterEllipsisEndIndex + (!hasPrevious ? 1 : 0),
    });
  }
  if (getBeforeEllipsisSize() > 1) {
    items.splice(beforeEllipsisStartIndex, getBeforeEllipsisSize(), {
      variant: 'before-ellipsis',
      start: beforeEllipsisStartIndex + (!hasPrevious ? 1 : 0),
      end: beforeEllipsisEndIndex + (!hasPrevious ? 1 : 0),
    });
  }

  return items;
};
